import React, { useState, useEffect } from 'react';
import { Heart, Trophy, Star, Camera, Mic, Settings, Home, Book, Award, X, Volume2, VolumeX, Monitor, Smartphone } from 'lucide-react';

const PuptasticApp = () => {
  const [currentLevel, setCurrentLevel] = useState(1);
  const [puppyStats, setPuppyStats] = useState({
    happiness: 85,
    energy: 70,
    hunger: 60,
    training: 45,
    bond: 30,
    health: 90,
    cleanliness: 80,
    intelligence: 35,
    age: 1
  });
  
  const [gameState, setGameState] = useState({
    dailyStreak: 3,
    totalXP: 150,
    badges: ['First Steps', 'Good Boy'],
    unlockedBreeds: ['Labrador Retriever', 'Golden Retriever', 'French Bulldog', 'Cocker Spaniel', 'Yorkshire Terrier'],
    currentBreed: 'Labrador Retriever',
    unlockedTricks: ['sit', 'stay'],
    knownTricks: ['sit'],
    soundEnabled: true
  });
  
  const [activeTab, setActiveTab] = useState('home');
  const [currentActivity, setCurrentActivity] = useState(null);
  const [puppyMood, setPuppyMood] = useState('happy');
  const [isTraining, setIsTraining] = useState(false);
  const [trainingCommand, setTrainingCommand] = useState('');
  const [achievements, setAchievements] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [photoMode, setPhotoMode] = useState(false);
  const [layoutMode, setLayoutMode] = useState('mobile');
  const [puppyAnimation, setPuppyAnimation] = useState('idle');
  const [puppyPosition, setPuppyPosition] = useState({ x: 50, y: 60 });
  const [puppySize, setPuppySize] = useState('medium');
  const [showTrickMenu, setShowTrickMenu] = useState(false);
  const [currentTrick, setCurrentTrick] = useState('');

  const dogBreeds = {
    'Labrador Retriever': {
      emoji: '🦮',
      icon: '🏆🦮',
      mainImage: '🦮🌟',
      color: '#D4AF37',
      traits: { energy: 90, intelligence: 85, friendliness: 100 },
      description: 'Friendly family dog'
    },
    'French Bulldog': {
      emoji: '🐶',
      icon: '🇫🇷🐶',
      mainImage: '🐶👑',
      color: '#8B4513',
      traits: { energy: 60, intelligence: 70, friendliness: 85 },
      description: 'Small companion dog'
    },
    'Golden Retriever': {
      emoji: '🐕',
      icon: '🌞🐕',
      mainImage: '🐕🌟☀️',
      color: '#DAA520',
      traits: { energy: 85, intelligence: 90, friendliness: 95 },
      description: 'Loyal and friendly'
    },
    'German Shepherd': {
      emoji: '🐺',
      icon: '🛡️🐺',
      mainImage: '🐺⚡🛡️',
      color: '#654321',
      traits: { energy: 80, intelligence: 100, friendliness: 70 },
      description: 'Noble working dog'
    },
    'Border Collie': {
      emoji: '🐕‍🦺',
      icon: '🧠🐕‍🦺',
      mainImage: '🐕‍🦺🧠⚡',
      color: '#2F4F4F',
      traits: { energy: 100, intelligence: 100, friendliness: 80 },
      description: 'Smartest breed'
    },
    'Cocker Spaniel': {
      emoji: '🐶',
      icon: '🌊🐶',
      mainImage: '🐶🌊✨💫',
      color: '#D2691E',
      traits: { energy: 75, intelligence: 80, friendliness: 90 },
      description: 'Loving sporting dogs with beautiful wavy coats'
    },
    'Yorkshire Terrier': {
      emoji: '🐕',
      icon: '👑🐕',
      mainImage: '🐕👑💎✨',
      color: '#8B4513',
      traits: { energy: 85, intelligence: 75, friendliness: 70 },
      description: 'Small but mighty with bold personalities'
    },
    'Jack Russell Terrier': {
      emoji: '🐶',
      icon: '⚡🐶',
      mainImage: '🐶⚡🔥💨',
      color: '#FFFFFF',
      traits: { energy: 100, intelligence: 85, friendliness: 75 },
      description: 'Highly energetic with fearless attitudes (100 energy!)'
    },
    'Pomeranian': {
      emoji: '🐕',
      icon: '☁️🐕',
      mainImage: '🐕☁️✨💫🎀',
      color: '#FF6347',
      traits: { energy: 70, intelligence: 75, friendliness: 85 },
      description: 'Tiny fluffy companions with confident personalities'
    },
    'Cavalier King Charles Spaniel': {
      emoji: '🐶',
      icon: '👑💎🐶',
      mainImage: '🐶👑💎🏰✨',
      color: '#8B4513',
      traits: { energy: 65, intelligence: 70, friendliness: 95 },
      description: 'Gentle toy spaniels with royal charm'
    }
  };

  const activities = [
    { 
      id: 'feed', 
      name: 'Feed', 
      icon: '🍖', 
      duration: 2000,
      effect: { hunger: -35, happiness: 8, health: 5 },
      requirements: { hunger: 30 }
    },
    { 
      id: 'play_fetch', 
      name: 'Fetch', 
      icon: '🎾', 
      duration: 3000,
      effect: { energy: -20, happiness: 20, bond: 5, intelligence: 2 },
      requirements: { energy: 20 }
    },
    { 
      id: 'groom', 
      name: 'Groom', 
      icon: '🛁', 
      duration: 4000,
      effect: { happiness: 12, bond: 3, cleanliness: 30, health: 3 },
      requirements: { cleanliness: 60 }
    },
    { 
      id: 'walk', 
      name: 'Walk', 
      icon: '🚶', 
      duration: 5000,
      effect: { energy: 15, happiness: 12, bond: 3, health: 8 },
      requirements: { energy: 70 }
    },
    { 
      id: 'train_basic', 
      name: 'Training', 
      icon: '🎯', 
      duration: 6000,
      effect: { training: 8, bond: 6, intelligence: 5, energy: -10 },
      requirements: { energy: 30 }
    },
    { 
      id: 'rest', 
      name: 'Nap', 
      icon: '😴', 
      duration: 3000,
      effect: { energy: 35, happiness: 5, health: 5 },
      requirements: {}
    }
  ];

  const allTricks = {
    sit: { name: 'Sit', difficulty: 1, requiredTraining: 10, requiredBond: 20 },
    stay: { name: 'Stay', difficulty: 2, requiredTraining: 25, requiredBond: 30 },
    come: { name: 'Come', difficulty: 2, requiredTraining: 20, requiredBond: 25 },
    down: { name: 'Down', difficulty: 2, requiredTraining: 30, requiredBond: 35 },
    jump: { name: 'Jump', difficulty: 3, requiredTraining: 40, requiredBond: 45 },
    rollover: { name: 'Roll Over', difficulty: 3, requiredTraining: 50, requiredBond: 60 },
    spin: { name: 'Spin', difficulty: 4, requiredTraining: 70, requiredBond: 70 },
    hide: { name: 'Hide', difficulty: 5, requiredTraining: 85, requiredBond: 90 }
  };

  const levels = [
    { id: 1, name: "New Puppy Parent", goal: "Feed and pet your puppy", unlocked: true },
    { id: 2, name: "Basic Care", goal: "Keep happiness above 60", unlocked: currentLevel >= 2 },
    { id: 3, name: "First Commands", goal: "Teach sit command", unlocked: currentLevel >= 3 },
    { id: 4, name: "Smart Trainer", goal: "Train with Border Collie", unlocked: currentLevel >= 4 },
    { id: 5, name: "High Energy", goal: "Handle Jack Russell energy", unlocked: currentLevel >= 5 },
    { id: 6, name: "Tiny Companion", goal: "Care for Pomeranian", unlocked: currentLevel >= 6 },
    { id: 7, name: "Royal Treatment", goal: "Pamper King Charles", unlocked: currentLevel >= 7 },
    { id: 8, name: "Breed Master", goal: "Master all breeds", unlocked: currentLevel >= 8 }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPuppyStats(prev => ({
        ...prev,
        hunger: Math.max(0, prev.hunger - 0.8),
        energy: Math.max(0, prev.energy - 0.4),
        cleanliness: Math.max(0, prev.cleanliness - 0.3),
        happiness: prev.hunger < 20 || prev.cleanliness < 30 ? 
          Math.max(0, prev.happiness - 1.2) : 
          prev.happiness < 95 ? prev.happiness + 0.1 : prev.happiness
      }));
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const avgStats = (puppyStats.happiness + puppyStats.energy + puppyStats.health + (100 - puppyStats.hunger)) / 4;
    if (avgStats > 75) setPuppyMood('happy');
    else if (avgStats > 50) setPuppyMood('neutral');
    else if (avgStats > 25) setPuppyMood('sad');
    else setPuppyMood('sick');

    if (puppyStats.age < 3) setPuppySize('small');
    else if (puppyStats.age < 8) setPuppySize('medium');
    else setPuppySize('large');
  }, [puppyStats]);

  const canPerformActivity = (activity) => {
    return Object.entries(activity.requirements).every(([stat, minValue]) => 
      stat === 'hunger' ? puppyStats.hunger > minValue : puppyStats[stat] > minValue
    );
  };

  const performActivity = (activity) => {
    if (!canPerformActivity(activity) || currentActivity) return;

    setCurrentActivity(activity.id);
    setPuppyAnimation(activity.id);
    
    setPuppyStats(prev => {
      const newStats = { ...prev };
      Object.keys(activity.effect).forEach(stat => {
        newStats[stat] = Math.max(0, Math.min(100, prev[stat] + activity.effect[stat]));
      });
      return newStats;
    });

    setTimeout(() => {
      setPuppyAnimation('idle');
      setCurrentActivity(null);
    }, activity.duration);

    checkAchievements(activity.id);
    checkLevelProgression();
  };

  const checkAchievements = (activityId) => {
    const newAchievements = [];
    
    if (activityId === 'feed' && puppyStats.hunger < 10) {
      newAchievements.push("Well Fed Champion");
    }
    if (puppyStats.bond > 80 && !gameState.badges.includes("Unbreakable Bond")) {
      newAchievements.push("Unbreakable Bond");
    }
    if (puppyStats.training > 90 && !gameState.badges.includes("Master Trainer")) {
      newAchievements.push("Master Trainer");
    }
    
    // Special breed-specific achievements
    if (gameState.currentBreed === 'Jack Russell Terrier' && puppyStats.energy < 20) {
      if (!gameState.badges.includes("Energy Tamer")) {
        newAchievements.push("Energy Tamer - Tired out a Jack Russell!");
      }
    }
    if (gameState.currentBreed === 'Border Collie' && puppyStats.intelligence > 95) {
      if (!gameState.badges.includes("Genius Trainer")) {
        newAchievements.push("Genius Trainer - Maxed Border Collie intelligence!");
      }
    }
    if (gameState.currentBreed === 'Cavalier King Charles Spaniel' && puppyStats.bond > 95) {
      if (!gameState.badges.includes("Royal Companion")) {
        newAchievements.push("Royal Companion - Perfect bond with royalty!");
      }
    }
    
    if (newAchievements.length > 0) {
      setAchievements(prev => [...prev, ...newAchievements]);
      setGameState(prev => ({
        ...prev,
        badges: [...prev.badges, ...newAchievements],
        totalXP: prev.totalXP + (newAchievements.length * 25)
      }));
    }
  };

  const checkLevelProgression = () => {
    const newLevel = Math.floor(gameState.totalXP / 100) + 1;
    if (newLevel > currentLevel) {
      setCurrentLevel(newLevel);
      setAchievements(prev => [...prev, `Level ${newLevel} Reached!`]);
      
      // Unlock new breeds based on level
      const breedUnlockLevels = {
        3: ['German Shepherd'],
        4: ['Border Collie'],
        5: ['Jack Russell Terrier'],
        6: ['Pomeranian'],
        7: ['Cavalier King Charles Spaniel']
      };
      
      if (breedUnlockLevels[newLevel]) {
        const newBreeds = breedUnlockLevels[newLevel].filter(
          breed => !gameState.unlockedBreeds.includes(breed)
        );
        
        if (newBreeds.length > 0) {
          setGameState(prev => ({
            ...prev,
            unlockedBreeds: [...prev.unlockedBreeds, ...newBreeds]
          }));
          
          newBreeds.forEach(breed => {
            setAchievements(prev => [...prev, `🎉 New Breed Unlocked: ${breed}!`]);
          });
        }
      }
    }
  };

  const getAvailableTricks = () => {
    return Object.entries(allTricks).filter(([trickId, trick]) => 
      puppyStats.training >= trick.requiredTraining && 
      puppyStats.bond >= trick.requiredBond
    );
  };

  const startTrickTraining = (trickId) => {
    setIsTraining(true);
    setCurrentTrick(trickId);
    setTrainingCommand(allTricks[trickId].name);
    setShowTrickMenu(false);
    
    setTimeout(() => {
      setIsTraining(false);
      const success = Math.random() < 0.7;
      
      if (success) {
        if (!gameState.knownTricks.includes(trickId)) {
          setGameState(prev => ({
            ...prev,
            knownTricks: [...prev.knownTricks, trickId]
          }));
          setAchievements(prev => [...prev, `Learned ${allTricks[trickId].name}!`]);
        }
        
        setPuppyStats(prev => ({
          ...prev,
          training: Math.min(100, prev.training + 15),
          bond: Math.min(100, prev.bond + 10),
          intelligence: Math.min(100, prev.intelligence + 5),
          happiness: Math.min(100, prev.happiness + 8)
        }));
        setPuppyAnimation('success');
      } else {
        setPuppyAnimation('confused');
        setPuppyStats(prev => ({
          ...prev,
          training: Math.min(100, prev.training + 3),
          bond: Math.min(100, prev.bond + 2)
        }));
      }
      
      setTimeout(() => {
        setPuppyAnimation('idle');
        setCurrentTrick('');
      }, 3000);
    }, 4000);
  };

  const changeBreed = (breed) => {
    setGameState(prev => ({ ...prev, currentBreed: breed }));
    setPuppyAnimation('transform');
    setTimeout(() => setPuppyAnimation('idle'), 2000);
  };

  const StatBar = ({ label, value, color, icon, warning }) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-lg">{icon}</span>
      <div className="flex-1">
        <div className="flex justify-between text-sm mb-1">
          <span className={`font-medium ${warning && value < 30 ? 'text-red-500' : 'text-gray-800'}`}>
            {label}
          </span>
          <span className={`${warning && value < 30 ? 'text-red-500' : 'text-gray-700'}`}>
            {Math.round(value)}/100
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ${
              warning && value < 30 ? 'bg-red-500' : color
            }`}
            style={{ width: `${Math.max(0, value)}%` }}
          />
        </div>
      </div>
    </div>
  );

  const getCurrentPuppyImage = () => {
    const breed = dogBreeds[gameState.currentBreed];
    
    if (puppyAnimation === 'idle' || puppyAnimation === puppyMood) {
      return breed.mainImage;
    }
    
    if (puppyAnimation === 'jump') {
      return breed.emoji + '🚀💫';
    } else if (puppyAnimation === 'spin') {
      return '🌪️' + breed.emoji + '💨';
    } else if (puppyAnimation === 'hide') {
      return '🙈' + breed.emoji + '✨';
    } else if (puppyAnimation === 'success') {
      return '⭐' + breed.emoji + '🏆✨';
    }
    
    return breed.mainImage;
  };

  const PuppyDisplay = () => (
    <div className={`relative ${layoutMode === 'desktop' ? 'h-96' : 'h-80'} bg-gradient-to-b from-blue-200 via-pink-100 to-yellow-200 rounded-lg overflow-hidden border-2 border-pink-300 shadow-lg`}>
      <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-r from-green-400 to-lime-400 rounded-b-lg"></div>
      
      <div className="absolute top-4 right-4 text-3xl animate-pulse">☀️</div>
      <div className="absolute top-6 left-4 text-2xl">🌳</div>
      <div className="absolute bottom-20 left-8 text-lg">🌸</div>
      
      <div 
        className={`absolute transition-all duration-1000 ${layoutMode === 'desktop' ? 'text-8xl' : 'text-6xl'} ${
          puppyAnimation === 'jump' ? 'animate-bounce transform -translate-y-8' : 
          puppyAnimation === 'spin' ? 'animate-spin' :
          puppyAnimation === 'hide' ? 'animate-pulse opacity-70 transform scale-90' :
          puppyAnimation === 'success' ? 'animate-bounce text-yellow-400 scale-110' :
          puppyMood === 'happy' ? 'hover:animate-bounce' : ''
        }`}
        style={{ 
          left: `${puppyPosition.x}%`, 
          top: `${puppyPosition.y}%`,
          transform: 'translate(-50%, -50%)'
        }}
        onClick={() => {
          if (!currentActivity) {
            setPuppyAnimation('happy');
            setPuppyStats(prev => ({ ...prev, happiness: Math.min(100, prev.happiness + 2) }));
            setTimeout(() => setPuppyAnimation('idle'), 1000);
          }
        }}
      >
        {getCurrentPuppyImage()}
        
        {currentActivity && (
          <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-white rounded-full px-3 py-2 shadow-lg animate-bounce border-2 border-pink-300">
            <span className="text-2xl">
              {activities.find(a => a.id === currentActivity)?.icon}
            </span>
          </div>
        )}
      </div>

      {isTraining && (
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 bg-opacity-30 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 shadow-2xl border-4 border-yellow-300 max-w-xs">
            <div className="text-center">
              <Mic className="mx-auto mb-3 text-red-500 animate-pulse" size={32} />
              <h3 className="font-bold text-xl mb-2 text-gray-800">Training Session</h3>
              <p className="font-bold text-lg text-blue-600 mb-3">"{trainingCommand}"</p>
            </div>
          </div>
        </div>
      )}

      {photoMode && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="text-white text-center">
            <Camera size={48} className="mx-auto mb-4 animate-pulse text-yellow-400" />
            <p className="text-lg font-bold">Say cheese! 📸</p>
            <button 
              onClick={() => {
                setPhotoMode(false);
                setAchievements(prev => [...prev, "Picture Perfect!"]);
              }}
              className="mt-4 bg-gradient-to-r from-pink-500 to-red-500 text-white px-4 py-2 rounded-lg font-bold hover:from-pink-600 hover:to-red-600 transition-all"
            >
              Snap Photo!
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const SettingsModal = () => (
    showSettings && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 w-full max-w-sm border-4 border-yellow-400 shadow-2xl">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Settings</h2>
            <button onClick={() => setShowSettings(false)} className="text-gray-600 hover:text-red-500">
              <X size={24} />
            </button>
          </div>
          
          <div className="flex items-center justify-between mb-4 p-3 bg-gradient-to-r from-pink-50 to-red-50 rounded-lg border border-pink-200">
            <span className="font-medium text-gray-800">Sound Effects</span>
            <button 
              onClick={() => setGameState(prev => ({ ...prev, soundEnabled: !prev.soundEnabled }))}
              className={`p-2 rounded-lg transition-all ${gameState.soundEnabled ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}
            >
              {gameState.soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
            </button>
          </div>

          <div className="mb-4">
            <h3 className="font-medium mb-2 text-gray-800">Choose Breed</h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {gameState.unlockedBreeds.map(breed => (
                <button
                  key={breed}
                  onClick={() => changeBreed(breed)}
                  className={`w-full p-3 rounded-lg border-2 transition-colors ${
                    gameState.currentBreed === breed 
                      ? 'border-pink-500 bg-gradient-to-r from-pink-50 to-red-50' 
                      : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{dogBreeds[breed].icon}</span>
                    <span className="font-medium text-gray-800">{breed}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  );

  const TrickMenu = () => (
    showTrickMenu && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 w-full max-w-sm max-h-96 overflow-y-auto border-4 border-yellow-400 shadow-2xl">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Available Tricks</h2>
            <button onClick={() => setShowTrickMenu(false)} className="text-gray-600 hover:text-red-500">
              <X size={24} />
            </button>
          </div>
          
          <div className="space-y-3">
            {getAvailableTricks().map(([trickId, trick]) => (
              <div key={trickId} className="border border-gray-200 rounded-lg p-3 hover:border-pink-300 transition-colors">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium text-gray-800">{trick.name}</h3>
                    <p className="text-sm text-gray-600">
                      Difficulty: {'⭐'.repeat(trick.difficulty)}
                    </p>
                  </div>
                  <button
                    onClick={() => startTrickTraining(trickId)}
                    disabled={isTraining}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:bg-gray-400 text-white px-3 py-1 rounded text-sm transition-all"
                  >
                    Train
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  );

  return (
    <div className="max-w-md mx-auto bg-gradient-to-br from-pink-100 via-yellow-50 to-blue-100 min-h-screen">
      <div className="bg-gradient-to-r from-red-400 to-pink-500 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Puptastic</h1>
            <p className="text-pink-100 text-sm">Level {currentLevel} • {gameState.currentBreed}</p>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setPhotoMode(true)}
              className="cursor-pointer hover:bg-pink-600 p-2 rounded-lg transition-colors"
            >
              <Camera size={24} />
            </button>
            <button 
              onClick={() => setShowSettings(true)}
              className="cursor-pointer hover:bg-pink-600 p-2 rounded-lg transition-colors"
            >
              <Settings size={24} />
            </button>
          </div>
        </div>
        
        <div className="mt-3 bg-pink-600 rounded-full h-3 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-3 rounded-full transition-all duration-500 shadow-sm"
            style={{ width: `${(gameState.totalXP % 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-pink-100 mt-1">
          <span>XP: {gameState.totalXP}</span>
          <span>Next Level: {100 - (gameState.totalXP % 100)} XP</span>
        </div>
      </div>

      {achievements.length > 0 && (
        <div className="absolute top-24 left-4 right-4 z-20 space-y-2">
          {achievements.map((achievement, index) => (
            <div 
              key={index}
              className="bg-gradient-to-r from-yellow-400 to-red-500 text-white p-4 rounded-xl shadow-lg animate-bounce cursor-pointer transform hover:scale-105 transition-transform border-2 border-white"
              onClick={() => setAchievements(prev => prev.filter((_, i) => i !== index))}
            >
              <div className="flex items-center gap-3">
                <Trophy size={24} />
                <div>
                  <div className="font-bold text-lg">Achievement Unlocked!</div>
                  <div className="text-yellow-100">{achievement}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="p-4 pb-20">
        {activeTab === 'home' && (
          <div className="space-y-4">
            <PuppyDisplay />
            
            <div className="bg-white rounded-lg p-4 shadow-sm border border-pink-200">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-800">
                <Heart className="text-red-500" size={20} />
                Puppy Status
              </h3>
              <StatBar label="Happiness" value={puppyStats.happiness} color="bg-yellow-400" icon="😊" warning />
              <StatBar label="Energy" value={puppyStats.energy} color="bg-blue-400" icon="⚡" warning />
              <StatBar label="Hunger" value={100 - puppyStats.hunger} color="bg-red-400" icon="🍖" warning />
              <StatBar label="Health" value={puppyStats.health} color="bg-green-400" icon="💚" warning />
              <StatBar label="Training" value={puppyStats.training} color="bg-purple-400" icon="🎯" />
              <StatBar label="Bond" value={puppyStats.bond} color="bg-pink-400" icon="💕" />
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm border border-pink-200">
              <h3 className="font-bold text-lg mb-3 text-gray-800">Activities</h3>
              <div className="grid grid-cols-3 gap-2">
                {activities.map(activity => {
                  const canPerform = canPerformActivity(activity);
                  return (
                    <button
                      key={activity.id}
                      onClick={() => performActivity(activity)}
                      disabled={currentActivity || !canPerform}
                      className={`flex flex-col items-center p-3 rounded-lg transition-colors ${
                        canPerform 
                          ? 'bg-gradient-to-br from-green-50 to-lime-50 hover:from-green-100 hover:to-lime-100 border-green-200 border' 
                          : 'bg-gray-100 border-gray-200 border opacity-60'
                      } ${currentActivity ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span className="text-2xl mb-1">{activity.icon}</span>
                      <span className="text-xs font-medium text-center text-gray-800">{activity.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm border border-pink-200">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-800">
                <Mic className="text-blue-500" size={20} />
                Trick Training
              </h3>
              
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setShowTrickMenu(true)}
                  disabled={isTraining}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:bg-gray-400 text-white font-medium py-2 rounded-lg transition-all"
                >
                  Choose Trick
                </button>
                <button
                  onClick={() => startTrickTraining('sit')}
                  disabled={isTraining}
                  className="bg-gradient-to-r from-green-500 to-lime-500 hover:from-green-600 hover:to-lime-600 disabled:bg-gray-400 text-white font-medium py-2 px-3 rounded-lg transition-all text-sm"
                >
                  Sit
                </button>
              </div>

              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1 text-gray-800">Known Tricks ({gameState.knownTricks.length}):</p>
                <div className="flex flex-wrap gap-1">
                  {gameState.knownTricks.map(trick => (
                    <span key={trick} className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 px-2 py-1 rounded text-xs border border-blue-200">
                      {allTricks[trick]?.name}
                    </span>
                  ))}
                  {gameState.knownTricks.length === 0 && (
                    <span className="text-gray-400 italic">No tricks learned yet</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 shadow-sm border border-pink-200">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-800">
                <Trophy className="text-yellow-500" size={20} />
                Level Progress
              </h3>
              <div className="space-y-3">
                {levels.map(level => (
                  <div 
                    key={level.id}
                    className={`p-3 rounded-lg border-2 ${
                      level.id === currentLevel ? 'border-pink-500 bg-gradient-to-r from-pink-50 to-red-50' :
                      level.unlocked ? 'border-gray-200 bg-gray-50' : 'border-gray-100 bg-gray-25'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-800">{level.name}</h4>
                        <p className="text-sm text-gray-600">{level.goal}</p>
                      </div>
                      {level.id === currentLevel && <Star className="text-yellow-500" size={20} />}
                      {level.id < currentLevel && <div className="text-green-500 text-xl">✓</div>}
                      {!level.unlocked && <div className="text-gray-400 text-xl">🔒</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm border border-pink-200">
              <h3 className="font-bold text-lg mb-3 text-gray-800">Breed Collection ({Object.keys(dogBreeds).length} Total)</h3>
              <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto">
                {Object.entries(dogBreeds).map(([breed, data]) => (
                  <div 
                    key={breed}
                    className={`p-3 rounded-lg border-2 ${
                      gameState.unlockedBreeds.includes(breed) 
                        ? gameState.currentBreed === breed 
                          ? 'border-pink-500 bg-gradient-to-br from-pink-50 to-red-50' 
                          : 'border-gray-200 bg-gray-50 cursor-pointer hover:border-pink-300'
                        : 'border-gray-100 bg-gray-25 opacity-60'
                    }`}
                    onClick={() => gameState.unlockedBreeds.includes(breed) && changeBreed(breed)}
                  >
                    <div className="text-center">
                      <div className="text-3xl mb-2">
                        {gameState.unlockedBreeds.includes(breed) ? data.icon : '🔒'}
                      </div>
                      <h4 className="font-medium text-xs mb-1 text-gray-800">{breed}</h4>
                      {gameState.unlockedBreeds.includes(breed) && (
                        <div className="text-xs text-gray-600">
                          <div className="grid grid-cols-3 gap-1 text-xs mb-1">
                            <div>⚡{data.traits.energy}</div>
                            <div>🧠{data.traits.intelligence}</div>
                            <div>❤️{data.traits.friendliness}</div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {data.description}
                          </p>
                        </div>
                      )}
                      {!gameState.unlockedBreeds.includes(breed) && (
                        <div className="text-xs text-gray-400 mt-1">
                          {breed === 'German Shepherd' && 'Unlock at Level 3'}
                          {breed === 'Border Collie' && 'Unlock at Level 4'}
                          {breed === 'Jack Russell Terrier' && 'Unlock at Level 5'}
                          {breed === 'Pomeranian' && 'Unlock at Level 6'}
                          {breed === 'Cavalier King Charles Spaniel' && 'Unlock at Level 7'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-3 text-center">
                <div className="text-sm text-gray-600">
                  Unlocked: {gameState.unlockedBreeds.length}/{Object.keys(dogBreeds).length} breeds
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-gradient-to-r from-pink-500 to-red-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(gameState.unlockedBreeds.length / Object.keys(dogBreeds).length) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-pink-200 shadow-lg">
        <div className="max-w-md mx-auto flex">
          <button
            onClick={() => setActiveTab('home')}
            className={`flex-1 flex flex-col items-center py-3 transition-colors ${
              activeTab === 'home' ? 'text-pink-600 bg-gradient-to-t from-pink-50 to-white' : 'text-gray-500 hover:text-pink-500'
            }`}
          >
            <Home size={20} />
            <span className="text-xs mt-1 font-medium">Home</span>
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`flex-1 flex flex-col items-center py-3 transition-colors ${
              activeTab === 'progress' ? 'text-pink-600 bg-gradient-to-t from-pink-50 to-white' : 'text-gray-500 hover:text-pink-500'
            }`}
          >
            <Book size={20} />
            <span className="text-xs mt-1 font-medium">Progress</span>
          </button>
        </div>
      </div>

      <SettingsModal />
      <TrickMenu />
    </div>
  );
};

export default PuptasticApp;
